import { D, DOCUMENT_TYPE_META, FINAL_CONSUMER_NAME, fiscalDateTime, formatMoney, PAYMENT_METHODS, regimeNotice, softwareMention, type VatRegime } from '@makila/fiscal';
import type { CashReport, IssuedDocument } from '@makila/pos-core';
import type { DeviceState } from './device';
import { create as qrCreate } from 'qrcode';

/**
 * Talão térmico (58 ou 80 mm) em HTML, impresso pela impressão do sistema —
 * serve qualquer impressora com driver instalado, sem rede (proposta §4: PWA).
 * Elementos do CIVA art. 27 n.º 5: emitente e NUIT, n.º e data, artigos,
 * base, taxa e IVA por taxa, total; "IVA incluído" (art. 30); menção do
 * regime (art. 45); 4 caracteres da assinatura e "Processado por computador".
 * O retrato da empresa é o que o terminal recebeu na activação.
 */

/** Data e hora de Maputo (nunca a do fuso do equipamento): "20/10/2026 22:31". */
/** Data AAAA-MM-DD (como vem da BD) em DD/MM/AAAA. */
export const mzDate = (d: string) => d.slice(0, 10).split('-').reverse().join('/');
export const mzDateTime = (iso: string) => { const f = fiscalDateTime(new Date(iso)); return `${f.slice(0, 10).split('-').reverse().join('/')} ${f.slice(11, 16)}`; };

export const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
const dmy = (d: string) => d.split('-').reverse().join('/');

export type PaperWidth = 58 | 80;

/** Vale-oferta: o código em grande, o valor e a validade; adiantamento sem IVA (o IVA sai na compra). */
function voucherBody(doc: IssuedDocument, pays: string): string {
  const v = doc.voucher!;
  return `<hr><div class="c"><b>VALE-OFERTA</b></div>
    <div class="c" style="font-size:2em;font-weight:700;letter-spacing:.08em;margin:6px 0">${esc(v.code)}</div>
    <table><tr class="big"><td>VALOR MT</td><td class="r">${formatMoney(doc.grossTotal)}</td></tr></table>
    <div class="c">${v.expiresAt ? `Válido até ${dmy(v.expiresAt)}` : 'Sem prazo de validade'}</div>
    <div><small>Adiantamento sem IVA: o IVA é facturado na compra em que o vale for usado. Não é reembolsável em dinheiro.</small></div><hr>
    <table>${pays}<tr><td>Entregue</td><td class="r">${formatMoney(doc.tendered)}</td></tr>
    ${new D(doc.change).greaterThan(0) ? `<tr><td><b>Troco</b></td><td class="r"><b>${formatMoney(doc.change)}</b></td></tr>` : ''}</table><hr>`;
}

/** Reserva (decisão F4): o que fica guardado, o que já foi pago e o prazo para levantar. */
function advanceNote(doc: IssuedDocument): string {
  const a = doc.advance!;
  const paid = new D(a.paidBefore).plus(doc.grossTotal);
  const rest = D.max(new D(a.total).minus(paid), 0);
  return `<div class="c"><b>RESERVA ${esc(a.code)}</b></div>
    <table>${a.items.map((i) => `<tr><td>${esc(new D(i.quantity).toString().replace('.', ','))} × ${esc(i.description)}</td></tr>`).join('')}</table>
    <table><tr><td>Total da reserva</td><td class="r">${formatMoney(a.total)}</td></tr>
      ${new D(a.paidBefore).greaterThan(0) ? `<tr><td>Pago antes</td><td class="r">${formatMoney(a.paidBefore)}</td></tr>` : ''}
      <tr><td>Este adiantamento</td><td class="r">${formatMoney(doc.grossTotal)}</td></tr>
      <tr class="big"><td>FALTA MT</td><td class="r">${formatMoney(rest)}</td></tr></table>
    <div>Levantar até <b>${dmy(a.dueDate)}</b>. O adiantamento é abatido na factura do levantamento.</div><hr>`;
}

/** QR em SVG (quadrados pretos num caminho): nítido em qualquer impressora. */
export function qrSvg(text: string, sizeMm = 34): string {
  const m = qrCreate(text, { errorCorrectionLevel: 'M' }).modules;
  let d = '';
  for (let r = 0; r < m.size; r++) for (let c = 0; c < m.size; c++) if (m.get(r, c)) d += `M${c + 2} ${r + 2}h1v1h-1z`;
  const n = m.size + 4;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${n} ${n}" width="${sizeMm}mm" height="${sizeMm}mm" shape-rendering="crispEdges"><rect width="${n}" height="${n}" fill="#fff"/><path d="${d}" fill="#000"/></svg>`;
}

/** Bilhetes (venda antecipada): um por pessoa ou grupo, com o QR que a porta lê. */
function ticketsHtml(doc: IssuedDocument, company: string): string {
  return (doc.tickets ?? []).map((t, i, all) => `<div style="border-top:2px dashed #000;margin-top:6mm;padding-top:4mm" class="c">
    <div>${esc(company)}</div>
    <div style="font-size:1.4em;font-weight:700;margin:2px 0">BILHETE</div>
    <div><b>${esc(doc.lines[t.line]?.description ?? 'Entrada')}</b></div>
    <div>${t.eventDate ? `Noite de ${dmy(t.eventDate)}` : 'Válido para uma noite à escolha'} · ${t.admits === 1 ? '1 pessoa' : `${t.admits} pessoas`}</div>
    <div style="margin:3mm 0">${qrSvg(t.code)}</div>
    <div style="font-size:1.6em;font-weight:700;letter-spacing:.08em">${esc(t.code)}</div>
    <div><small>${esc(doc.fullNumber)} · ${i + 1}/${all.length} · entra uma vez</small></div>
  </div>`).join('');
}

/** Recibo: o valor recebido, como foi pago e o que fica em dívida (sem linhas nem IVA). */
function receiptBody(doc: IssuedDocument, pays: string): string {
  const before = new D(doc.receipt?.balanceBefore ?? doc.grossTotal);
  return `<hr><div>Recebemos por conta das facturas em aberto:</div>
    <table><tr class="big"><td>VALOR MT</td><td class="r">${formatMoney(doc.grossTotal)}</td></tr></table><hr>
    <table>${pays}<tr><td>Entregue</td><td class="r">${formatMoney(doc.tendered)}</td></tr>
    ${new D(doc.change).greaterThan(0) ? `<tr><td><b>Troco</b></td><td class="r"><b>${formatMoney(doc.change)}</b></td></tr>` : ''}</table><hr>
    <table><tr><td>Em dívida antes</td><td class="r">${formatMoney(before)}</td></tr><tr><td><b>Fica em dívida</b></td><td class="r"><b>${formatMoney(D.max(before.minus(doc.grossTotal), 0))}</b></td></tr></table><hr>`;
}

export function receiptHtml(doc: IssuedDocument, device: DeviceState, opts: { width?: PaperWidth; copy?: 'Original' | '2.ª Via'; staffNames?: Map<string, string> } = {}): string {
  const width = opts.width ?? 80;
  const c = device.company;
  const anyIncl = doc.lines.some((l) => l.priceIncludesTax);
  const regime = regimeNotice(c.vatRegime as VatRegime);
  const lines = doc.lines.map((l) => {
    const qty = new D(l.quantity);
    const by = l.staffId ? opts.staffNames?.get(l.staffId) : undefined;
    const sn = l.serial ? `<tr><td colspan="2"><small>N.º série ${esc(l.serial)}${l.warrantyUntil ? ` · garantia até ${dmy(l.warrantyUntil)}` : ''}</small></td></tr>` : '';
    return `<tr><td colspan="2">${esc(l.description)}${by ? ` <small>· ${esc(by)}</small>` : ''}</td></tr>${sn}
      <tr class="sub"><td>${esc(qty.toString().replace('.', ','))} × ${formatMoney(l.unitPrice)}${l.discountPct && !new D(l.discountPct).isZero() ? ` −${esc(String(l.discountPct))}%` : ''}${new D(l.taxRate).isZero() ? ` <small>(${esc(l.exemptionCode ?? 'isento')})</small>` : ''}</td><td class="r">${formatMoney(l.totalAmount)}</td></tr>`;
  }).join('');
  const taxes = doc.taxSummary.map((t) => `<tr><td>IVA ${new D(t.rate).toString().replace('.', ',')}% s/ ${formatMoney(t.netAmount)}</td><td class="r">${formatMoney(t.taxAmount)}</td></tr>`).join('');
  const pays = doc.payments.map((p) => `<tr><td>${esc(PAYMENT_METHODS[p.method])}${p.reference ? ` <small>${esc(p.reference)}</small>` : ''}</td><td class="r">${formatMoney(p.amount)}</td></tr>`).join('');
  const customer = doc.customer ?? { name: FINAL_CONSUMER_NAME, nuit: null };
  // O que o cliente poupou: preço × quantidade menos o cobrado, na base do preço.
  const savings = doc.lines.reduce((s, l) => s.plus(new D(l.quantity).times(l.unitPrice).toDecimalPlaces(2).minus(l.priceIncludesTax ? l.totalAmount : l.netAmount)), new D(0));

  return `<!doctype html><html lang="pt"><head><meta charset="utf-8"><title>${esc(doc.fullNumber)}</title><style>
    @page { size: ${width}mm auto; margin: 0; }
    * { box-sizing: border-box; }
    body { margin: 0; padding: 3mm ${width === 58 ? 2 : 4}mm; width: ${width}mm; font: ${width === 58 ? 10 : 11.5}px/1.3 ui-monospace, "Courier New", monospace; color: #000; }
    h1 { font-size: 1.25em; margin: 0; text-align: center; } .c { text-align: center; } .r { text-align: right; white-space: nowrap; }
    table { width: 100%; border-collapse: collapse; } td { padding: 0; vertical-align: top; } tr.sub td { padding-bottom: 2px; }
    hr { border: 0; border-top: 1px dashed #000; margin: 4px 0; } .big td { font-size: 1.3em; font-weight: 700; } small { font-size: .85em; }
    .train { border: 2px solid #000; padding: 2px; text-align: center; font-weight: 700; margin: 4px 0; }
  </style></head><body>
    <h1>${esc(c.tradeName ?? c.legalName)}</h1>
    ${c.tradeName && c.tradeName !== c.legalName ? `<div class="c">${esc(c.legalName)}</div>` : ''}
    <div class="c">NUIT ${esc(c.nuit)}</div>
    <div class="c">${esc([device.store.address || c.address, c.city].filter(Boolean).join(', '))}</div>
    ${device.store.name && ![c.tradeName, c.legalName].includes(device.store.name) ? `<div class="c">${esc(device.store.name)}</div>` : ''}
    <hr>
    ${doc.training ? '<div class="train">SEM VALOR FISCAL — MODO FORMAÇÃO</div>' : ''}
    ${doc.docType === 'PF' ? '<div class="train">PRÓ-FORMA — NÃO SERVE DE FACTURA</div>' : ''}
    <div><b>${esc(DOCUMENT_TYPE_META[doc.docType].label)} ${esc(doc.fullNumber)}</b>${opts.copy ? ` · ${esc(opts.copy)}` : ''}</div>
    ${doc.origin ? `<div>Referente a ${esc(doc.origin.fullNumber)} de ${dmy(doc.origin.issueDate)}</div><div>Motivo: ${esc(doc.reason ?? '')}</div>` : ''}
    ${doc.loyalty ? `<div>Pontos usados: ${doc.loyalty.pointsUsed}</div>` : ''}
    ${doc.prescription ? `<div>Receita n.º ${esc(doc.prescription.number)} de ${dmy(doc.prescription.date)} · ${esc(doc.prescription.prescriber)}</div>` : ''}
    <div>${dmy(doc.issueDate)} ${esc(doc.systemEntryDate.slice(11))} · ${esc(device.terminal.name)}</div>
    <div>Cliente: ${esc(customer.name)}${customer.nuit ? ` · NUIT ${esc(customer.nuit)}` : ''}</div>
    ${doc.docType === 'RE' && doc.voucher ? voucherBody(doc, pays) : doc.docType === 'RE' ? receiptBody(doc, pays) : `<hr><table>${lines}</table><hr>
    <table>
      <tr><td>Base tributável</td><td class="r">${formatMoney(doc.netTotal)}</td></tr>
      ${taxes}
      ${savings.greaterThan(0) ? `<tr><td>Descontos</td><td class="r">−${formatMoney(savings)}</td></tr>` : ''}
      <tr class="big"><td>TOTAL MT</td><td class="r">${formatMoney(doc.grossTotal)}</td></tr>
    </table>
    ${anyIncl ? '<div><small>IVA incluído</small></div>' : ''}
    ${doc.docType === 'PF' ? '<hr>'
      : doc.docType === 'FT' ? `<hr><div><b>A CRÉDITO</b> · vence a ${dmy(doc.dueDate ?? doc.issueDate)}</div><div style="margin-top:8mm">Recebi: ______________________</div><hr>`
      : doc.docType === 'NC' ? (doc.payments.length ? `<hr><div>Reembolso:</div><table>${pays}</table><hr>` : '<hr><div>Abatido na conta corrente do cliente</div><hr>')
      : `<hr><table>${pays}
      <tr><td>Entregue</td><td class="r">${formatMoney(doc.tendered)}</td></tr>
      ${new D(doc.change).greaterThan(0) ? `<tr><td><b>Troco</b></td><td class="r"><b>${formatMoney(doc.change)}</b></td></tr>` : ''}
    </table><hr>`}`}
    ${doc.advance ? advanceNote(doc) : ''}
    ${doc.reservation && doc.docType !== 'NC' ? `<div>Levantamento da reserva <b>${esc(doc.reservation.code)}</b>: adiantamentos abatidos ${formatMoney(doc.reservation.deducted ?? '0')} MT (notas de crédito à parte)</div><hr>` : ''}
    ${regime ? `<div class="c"><small>${esc(regime)}</small></div>` : ''}
    <div class="c"><small>${esc(softwareMention(doc.hash, device.softwareName))}${doc.training ? ' · documento emitido em modo de formação' : ''}</small></div>
    ${doc.docType === 'NC' ? '<div style="margin-top:10mm">Recebi: ______________________</div>' : '<div class="c" style="margin-top:4px">Obrigado pela preferência</div>'}
    ${doc.tickets?.length ? ticketsHtml(doc, c.tradeName ?? c.legalName) : ''}
  </body></html>`;
}

/** Imprime num iframe escondido: não sai da página da caixa nem perde o carrinho. */
export function printHtml(html: string): Promise<void> {
  return new Promise((resolve) => {
    const f = document.createElement('iframe');
    f.setAttribute('aria-hidden', 'true');
    f.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
    document.body.appendChild(f);
    const w = f.contentWindow!;
    w.document.open();
    w.document.write(html);
    w.document.close();
    const finish = () => { setTimeout(() => { f.remove(); resolve(); }, 500); };
    w.addEventListener('afterprint', finish, { once: true });
    setTimeout(() => { w.focus(); w.print(); setTimeout(finish, 60_000); }, 50);
  });
}

/** Relatório X ou Z em papel térmico (mesmo formato do talão). */
export function cashReportHtml(r: CashReport, device: DeviceState, operatorName: (id: string | null) => string, opts: { width?: PaperWidth } = {}): string {
  const width = opts.width ?? 80;
  const c = device.company;
  const names = { openedBy: operatorName(r.openedBy), closedBy: operatorName(r.closedBy), op: (id: string) => operatorName(id) };
  const dt = (iso: string | null) => (iso ? mzDateTime(iso) : '—');
  const row = (a: string, b: string, strong = false) => `<tr${strong ? ' class="big"' : ''}><td>${esc(a)}</td><td class="r">${esc(b)}</td></tr>`;
  // Textos longos (data · operador, números de documento): na linha de baixo, a poder quebrar — senão saem do papel.
  const wide = (a: string, b: string) => `<tr><td colspan="2">${esc(a)}</td></tr><tr><td colspan="2" class="r wrap">${esc(b)}</td></tr>`;
  const types = Object.entries(r.documents.byType).map(([t, v]) => row(`${DOCUMENT_TYPE_META[t as keyof typeof DOCUMENT_TYPE_META]?.label ?? t} (${v.count})`, formatMoney(v.total))).join('');
  return `<!doctype html><html lang="pt"><head><meta charset="utf-8"><title>Relatório ${r.kind}</title><style>
    @page { size: ${width}mm auto; margin: 0; } * { box-sizing: border-box; }
    body { margin: 0; padding: 3mm ${width === 58 ? 2 : 4}mm; width: ${width}mm; font: ${width === 58 ? 10 : 11.5}px/1.3 ui-monospace, "Courier New", monospace; color: #000; }
    h1 { font-size: 1.3em; margin: 0; text-align: center; } .c { text-align: center; } .r { text-align: right; white-space: nowrap; } .wrap { white-space: normal; overflow-wrap: anywhere; }
    table { width: 100%; border-collapse: collapse; } td { padding: 0; } hr { border: 0; border-top: 1px dashed #000; margin: 4px 0; } .big td { font-weight: 700; }
  </style></head><body>
    <div class="c">${esc(c.tradeName ?? c.legalName)} · NUIT ${esc(c.nuit)}</div>
    <div class="c">${esc(device.store.name)} · ${esc(device.terminal.name)}</div>
    <hr><h1>RELATÓRIO ${r.kind}${r.zNumber ? ` N.º ${r.zNumber}` : ''}</h1>
    <div class="c">${r.kind === 'X' ? 'Leitura a meio — a caixa continua aberta' : 'Fecho de caixa'}</div><hr>
    <table>
      ${wide('Abertura', `${dt(r.openedAt)} · ${names.openedBy}`)}
      ${r.closedAt ? wide('Fecho', `${dt(r.closedAt)} · ${names.closedBy ?? ''}`) : ''}
      ${row('Documentos', String(r.documents.count))}
      ${r.documents.first ? wide('Do n.º', r.documents.first) : ''}
      ${r.documents.last ? wide('Ao n.º', r.documents.last) : ''}
    </table><hr><table>${types}${row('TOTAL VENDAS', formatMoney(r.grossTotal), true)}</table><hr>
    <table>${r.byMethod.map((m) => row(m.label, formatMoney(m.amount))).join('')}</table><hr>
    <table>${r.byTaxRate.map((t) => row(`IVA ${new D(t.rate).toString().replace('.', ',')}% s/ ${formatMoney(t.netAmount)}`, formatMoney(t.taxAmount))).join('')}</table><hr>
    <table>
      ${row('Fundo de caixa', formatMoney(r.openingFloat))}
      ${row('Reforços', formatMoney(r.cashIn))}
      ${row('Sangrias', `${new D(r.cashOut).isZero() ? '' : '−'}${formatMoney(r.cashOut)}`)}
      ${row('Aberturas de gaveta sem venda', String(r.drawerOpens))}
      ${(r.tips ?? []).map((t) => row(`Gorjetas — ${names.op(t.beneficiaryId)}`, `${formatMoney(t.cash)}${new D(t.other).isZero() ? '' : ` (+${formatMoney(t.other)} outros meios)`}`)).join('')}
      ${row('Numerário esperado', formatMoney(r.expectedCash), true)}
      ${r.countedCash !== null ? row('Numerário contado', formatMoney(r.countedCash), true) : ''}
      ${r.difference !== null ? row(new D(r.difference).isNegative() ? 'FALTA' : new D(r.difference).isZero() ? 'Diferença' : 'SOBRA', formatMoney(new D(r.difference).abs()), true) : ''}
    </table><hr>
    <div class="c">Impresso ${dt(new Date().toISOString())}</div>
    ${r.kind === 'Z' ? '<div style="margin-top:12mm">Assinatura: ______________________</div>' : ''}
  </body></html>`;
}
